import { useState } from "react";
import {
  Download,
  Link as LinkIcon,
  Youtube,
  Video,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Opcoes de qualidade
// ---------------------------------------------------------------------------

const QUALITY_OPTIONS = [
  { value: "highest", label: "Melhor qualidade (1080p/4K)" },
  { value: "hd", label: "HD (720p)" },
  { value: "sd", label: "SD (480p)" },
  { value: "audio", label: "Apenas áudio (MP3)" },
] as const;

type QualityValue = (typeof QUALITY_OPTIONS)[number]["value"];

// ---------------------------------------------------------------------------
// Plataformas suportadas
// ---------------------------------------------------------------------------

const SUPPORTED_PLATFORMS = [
  { label: "YouTube", icon: Youtube },
  { label: "Instagram", icon: Video },
  { label: "TikTok", icon: Video },
  { label: "Facebook", icon: Video },
  { label: "Twitter/X", icon: Video },
];

// ---------------------------------------------------------------------------
// DownloadsPage
// ---------------------------------------------------------------------------

export function DownloadsPage(): JSX.Element {
  const [url, setUrl] = useState("");
  const [quality, setQuality] = useState<QualityValue>("highest");
  const [downloadTriggered, setDownloadTriggered] = useState(false);

  function handleDownload(): void {
    if (!url.trim()) return;
    setDownloadTriggered(true);

    // Placeholder: futuramente chamara a API de download
    // Exemplo: fetch('/api/download', { method: 'POST', body: { url, quality } })
  }

  function handleUrlChange(value: string): void {
    setUrl(value);
    if (downloadTriggered) {
      setDownloadTriggered(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        title="Downloads"
        subtitle="Baixe vídeos de redes sociais para usar nas suas promoções e conteúdos."
      />

      {/* Campo de URL */}
      <Card>
        <CardContent className="space-y-5 pt-5">
          <div className="space-y-2">
            <label
              htmlFor="video-url"
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              URL do vídeo
            </label>
            <div className="relative">
              <LinkIcon
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="video-url"
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="Cole aqui o link do vídeo (YouTube, Instagram, TikTok...)"
                className="pl-9"
                autoComplete="off"
              />
            </div>
          </div>

          {/* Seletor de qualidade */}
          <div className="space-y-2">
            <label
              htmlFor="quality-select"
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Qualidade
            </label>
            <select
              id="quality-select"
              value={quality}
              onChange={(e) => setQuality(e.target.value as QualityValue)}
              className={cn(
                "h-10 w-full rounded-md border border-[hsl(var(--border-strong))]",
                "bg-[hsl(var(--surface-raised))] px-3 text-sm text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              {QUALITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Botao Baixar */}
          <Button
            onClick={handleDownload}
            disabled={!url.trim()}
            className="w-full gap-2 sm:w-auto"
          >
            <Download size={16} aria-hidden="true" />
            Baixar
          </Button>

          {/* Mensagem de placeholder */}
          {downloadTriggered && (
            <div className="rounded-lg border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-400">
              <p className="font-medium">Em breve: download via API</p>
              <p className="mt-1 text-xs text-amber-400/80">
                O serviço de download estará disponível em breve.
                Quando ativo, você poderá baixar vídeos diretamente
                pelo painel.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plataformas suportadas */}
      <Card>
        <CardContent className="space-y-4 pt-5">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Plataformas suportadas
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Em breve você poderá baixar vídeos das seguintes plataformas:
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {SUPPORTED_PLATFORMS.map((platform) => {
              const Icon = platform.icon;
              return (
                <div
                  key={platform.label}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg border border-border",
                    "bg-[hsl(var(--surface-raised)/0.5)] px-3.5 py-2",
                  )}
                >
                  <Icon
                    size={16}
                    className="text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="text-sm font-medium text-foreground">
                    {platform.label}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Instrucoes */}
      <Card>
        <CardContent className="space-y-2 pt-5">
          <h3 className="text-sm font-semibold text-foreground">
            Como funciona
          </h3>
          <ol className="ml-5 list-decimal space-y-1.5 text-sm text-muted-foreground">
            <li>Copie o link do vídeo que deseja baixar.</li>
            <li>Cole no campo acima e selecione a qualidade desejada.</li>
            <li>Clique em "Baixar" e aguarde o processamento.</li>
            <li>
              O vídeo será processado e disponibilizado para download
              no seu computador.
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
